import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import WayfinderEditor from "./WayfinderEditor";
import UseToast from "../UseToast";
import useS3Upload from "../../hooks/useS3Upload";
import useCurrentLanguage from "../../hooks/useCurrentLanguage";

const WayfinderCategory = () => {
  const navigate = useNavigate();
  const { userId, categoryId } = useParams();
  const { showErrorToast, showSuccessToast } = UseToast();

  // Language management
  const { currentLanguage } = useCurrentLanguage(userId);

  const [loading, setLoading] = useState(false);
  const [buttonColor, setButtonColor] = useState("#000000");

  const [iconFile, setIconFile] = useState(null);
  const [pngFile, setPngFile] = useState(null);
  const [promoVideoFile, setPromoVideoFile] = useState(null);
  const [clickSoundFile, setClickSoundFile] = useState(null);

  const { uploadFile: uploadToS3, uploading: s3Uploading } = useS3Upload({
    signedUrlEndpoint: "/generate-signed-url",
    onSuccess: (fileUrl, file) => {
      showSuccessToast(`${file.name} uploaded successfully`);
    },
    onError: () => {
      showErrorToast("Upload failed");
    },
  });

  const [item, setItem] = useState({
    name: "",
    description: "",
    status: true,
    promoVideo: "",
    promoVideoFilename: null,
    promoVideoEnabled: false,
    enabled: true,
    navigation: {
      directLocation: false,
      type: "location", // "directLocation", "subcategories", "locations", or "none"
      subcategories: [], // Array of subcategory IDs
      locations: [], // Array of location IDs
    },

    buttonText: "",

    // globalSettings: {
    //   cornerRadius: 0,
    //   height: 50,
    //   width: 150,
    //   outlineColor: '#000000',
    //   outlineThickness: 1,
    //   fillColor: '#ffffff',
    //   font: 'Arial',
    //   fontColor: '#127E10',
    //   fontSize: 16,
    //   textAlign: 'center',
    //   marginBottom: 2,
    //   lineHeight: 1,
    //   padding: 10,
    //   shadow: {
    //     angle: 5,
    //     size: 10,
    //     distance: 5,
    //     alpha: 0.5,
    //     color: '#FF5733',
    //   },
    //   highlight: {
    //     borderColor: '#000000',
    //     fillColor: '#ffffff',
    //     fontColor: '#000000',
    //   },
    //   clickSound: '',
    //   clickSoundFilename: null,
    //   clickSoundVolume: 1,
    // },

    customSettings: {
      cornerRadius: 0,
      height: 50,
      width: 150,
      outlineColor: "#000000",
      outlineThickness: 1,
      fillColor: "#ffffff",
      font: "Arial",
      fontColor: "#127E10",
      fontSize: 16,
      textAlign: "center",
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      shadow: {
        angle: 5,
        size: 10,
        distance: 5,
        alpha: 0.5,
        color: "#FF5733",
      },
      highlight: {
        borderColor: "#000000",
        fillColor: "#ffffff",
        fontColor: "#000000",
      },
      clickSound: "",
      clickSoundFilename: null,
      clickSoundVolume: 1,

      // Custom-only fields
      pngBtnenabled: false,
      hideBtnText: false,
      btnPng: null,
      btnPngFileName: null,
    },
  });

  const [selectedExistingSubCategories, setSelectedExistingSubCategories] =
    useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [locations, setLocations] = useState([]);

  // Step-by-Step Directions State
  const [stepByStepConfig, setStepByStepConfig] = useState({
    // No useGlobal for categories - they define their own config
    codeInsert1: '',
    opening: '',
    mapTimeDelay: null,
    codeInsert2: '',
    stepByStepOpening: '',
    stepByStepDirections: '',
    codeInsert3: '',
    closing: '',
    codeInsert4: '',
    globalQrClosing: {
      enabled: true,
      text: '',
    },
    codeInsert5: '',
  });

  // Add this useEffect to fetch subcategories and locations
  useEffect(() => {
    if (userId && categoryId) {
      fetchItemData();
      fetchSiteBrandColor();
      fetchSubcategories();
      fetchLocations();
      fetchStepByStepConfig();
    }
  }, [userId, categoryId]);

  // Function to fetch subcategories for this specific category
  const fetchSubcategories = async () => {
    try {
      const res = await axios.get(
        `/api/wayfinder/subcategory/category/${categoryId}`,
      );
      if (res.data.success) {
        setSubcategories(res.data.dataSub || []);

        console.log("subcategories", subcategories);
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  // Function to fetch locations
  const fetchLocations = async () => {
    try {
      const res = await axios.get(`/api/wayfinder/get-locations/${userId}`);
      if (res.data.success) {
        setLocations(res.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  // Fetch Step-by-Step Directions Configuration
  const fetchStepByStepConfig = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/step-by-step-directions/category/${userId}/${categoryId}`);
      if (response.data.success) {
        setStepByStepConfig(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching step-by-step directions:", error);
    }
  };

  // Save Step-by-Step Directions Configuration
  const saveStepByStepConfig = async () => {
    try {
      await axios.post(`/api/wayfinder/step-by-step-directions/category/${userId}/${categoryId}`, stepByStepConfig);
      showSuccessToast("Step-by-step directions configuration saved successfully!");
    } catch (error) {
      console.error("Error saving step-by-step directions:", error);
      showErrorToast("Failed to save step-by-step directions configuration");
    }
  };

  useEffect(() => {
    if (userId && categoryId) {
      fetchItemData();
      fetchSiteBrandColor();
    }
  }, [userId, categoryId]);

  const fetchSiteBrandColor = async () => {
    try {
      const res = await axios.get(`/auth/aiform/all//${userId}`);
      setButtonColor(res.data?.brand_color || "#000000");
    } catch (err) {
      console.error(err);
    }
  };

  const fetchItemData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/wayfinder/get-categories/${userId}`);
      const category = res.data?.data?.find((c) => c._id === categoryId);

      if (!category) {
        showErrorToast("Category not found");
        return;
      }

      // Helper function to extract IDs from potentially populated arrays
      const extractIds = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.map((item) => {
          if (typeof item === "string") return item;
          if (item && item._id) return item._id;
          return item;
        });
      };

      // Fix: Properly merge settings
      const categoryData = {
        ...category,
        enabled: category?.enabled ?? true,
        directLocation: category?.directLocation ?? false,
        buttonText: category?.buttonText ?? "",
        globalSettings: {
          ...item.globalSettings, // Keep defaults
          ...category?.globalSettings, // Override with saved data
        },
        customSettings: {
          ...item.customSettings, // Keep defaults
          ...category?.customSettings, // Override with saved data
        },
        locationSettings: {
          ...item.locationSettings,
          ...(category?.locationSettings || {}),
        },

        navigation: {
          type: category?.navigation?.type || "location",
          directLocation: category?.navigation?.directLocation || null,
          subcategories: extractIds(
            category?.navigation?.subcategories ||
            category?.subCategories ||
            [],
          ),
          locations: extractIds(
            category?.navigation?.locations || category?.locations || [],
          ),
        },
      };

      setItem(categoryData);

      // Set selected subcategories from the navigation data
      // If navigation.subcategories is empty, we'll auto-select all in a separate useEffect
      setSelectedExistingSubCategories(
        categoryData.navigation.subcategories || [],
      );
    } catch (err) {
      showErrorToast("Failed to load category");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select all subcategories if none are selected
  useEffect(() => {
    if (
      subcategories.length > 0 &&
      selectedExistingSubCategories.length === 0 &&
      item._id
    ) {
      const allSubcategoryIds = subcategories.map((sub) => sub._id);
      setSelectedExistingSubCategories(allSubcategoryIds);

      // Also update the item state
      setItem((prev) => ({
        ...prev,
        navigation: {
          ...prev.navigation,
          subcategories: allSubcategoryIds,
        },
      }));

      console.log("Auto-selected all subcategories:", allSubcategoryIds);
    }
  }, [subcategories, item._id]);

  // Fixed: Properly resolve button settings based on enabled state
  const resolvedButtonSettings = useMemo(() => {
    // If custom settings are enabled, merge them with global settings
    if (item.enabled) {
      return {
        ...item.globalSettings,
        ...item.customSettings,
      };
    }
    // Otherwise, only use global settings
    return item.globalSettings;
  }, [item.globalSettings, item.customSettings, item.enabled]);

  const { getRootProps: getIconRootProps, getInputProps: getIconInputProps } =
    useDropzone({
      accept: { "image/*": [] },
      maxFiles: 1,
      onDrop: ([file]) => {
        setIconFile(file);
      },
    });

  const { getRootProps: getPngRootProps, getInputProps: getPngInputProps } =
    useDropzone({
      accept: { "image/png": [] },
      maxFiles: 1,
      onDrop: ([file]) => {
        setPngFile(file);
      },
    });

  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps } =
    useDropzone({
      accept: { "video/*": [] },
      maxFiles: 1,
      onDrop: ([file]) => {
        setPromoVideoFile(file);
      },
    });

  const handleUploadIcon = async () => {
    if (!iconFile) {
      showErrorToast("No icon file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(iconFile);

      setItem((prev) => ({
        ...prev,
        icon: fileUrl,
      }));

      // Clear the file after upload
      setIconFile(null);
    } catch (error) {
      showErrorToast("Failed to upload icon");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPng = async () => {
    console.log("Png file selected", pngFile);

    if (!pngFile) {
      showErrorToast("No PNG file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(pngFile);

      setItem((prev) => {
        const existingBtnPng = prev.customSettings?.btnPng;
        const existingBtnPngFileName = prev.customSettings?.btnPngFileName;

        const updatedBtnPng = typeof existingBtnPng === 'object' && existingBtnPng !== null
          ? { ...existingBtnPng, [currentLanguage]: fileUrl }
          : { en: existingBtnPng || fileUrl, [currentLanguage]: fileUrl };

        const updatedBtnPngFileName = typeof existingBtnPngFileName === 'object' && existingBtnPngFileName !== null
          ? { ...existingBtnPngFileName, [currentLanguage]: pngFile.name }
          : { en: existingBtnPngFileName || pngFile.name, [currentLanguage]: pngFile.name };

        return {
          ...prev,
          customSettings: {
            ...prev.customSettings,
            btnPng: updatedBtnPng,
            btnPngFileName: updatedBtnPngFileName,
            pngBtnenabled: true,
          },
        };
      });

      // Clear the file after upload
      setPngFile(null);
    } catch (error) {
      showErrorToast("Failed to upload PNG");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClickSound = async () => {
    console.log("Click sound file selected", clickSoundFile);

    if (!clickSoundFile) {
      showErrorToast("No click sound file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(clickSoundFile);

      setItem((prev) => ({
        ...prev,
        customSettings: {
          ...prev.customSettings,
          clickSound: fileUrl,
          clickSoundFilename: clickSoundFile.name,
        },
      }));

      // Clear the file after upload
      setClickSoundFile(null);
    } catch (error) {
      showErrorToast("Failed to upload click sound");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPromoVideo = async () => {
    if (!promoVideoFile) {
      showErrorToast("No video file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(promoVideoFile);

      setItem((prev) => ({
        ...prev,
        promoVideo: fileUrl,
        promoVideoFilename: promoVideoFile.name,
        promoVideoEnabled: true,
      }));

      // Clear the file after upload
      setPromoVideoFile(null);
    } catch (error) {
      showErrorToast("Failed to upload video");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const res = await axios.patch(`/api/wayfinder/categories/${categoryId}`, {
        userId,
        ...item,
      });

      if (res.data.success) {
        showSuccessToast("Saved successfully");
        fetchItemData();
      } else {
        showErrorToast("Save failed");
      }
    } catch (error) {
      showErrorToast("Save failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this category permanently?")) return;

    try {
      await axios.delete(
        `/api/wayfinder/category/${categoryId}?userId=${userId}`,
      );
      showSuccessToast("Deleted successfully");
      navigate(`/wayfinder-main/${userId}`);
    } catch (error) {
      showErrorToast("Delete failed");
      console.error(error);
    }
  };

  return (
    <WayfinderEditor
      userId={userId}
      type="category"
      item={item}
      setItem={setItem}
      resolvedButtonSettings={resolvedButtonSettings}
      loading={loading || s3Uploading}
      buttonColor={buttonColor}
      iconFile={iconFile}
      pngFile={pngFile}
      promoVideoFile={promoVideoFile}
      getIconRootProps={getIconRootProps}
      getIconInputProps={getIconInputProps}
      getPngRootProps={getPngRootProps}
      getPngInputProps={getPngInputProps}
      getVideoRootProps={getVideoRootProps}
      getVideoInputProps={getVideoInputProps}
      handleUploadIcon={handleUploadIcon}
      handleUploadPng={handleUploadPng}
      handleUploadPromoVideo={handleUploadPromoVideo}
      handleSave={handleSave}
      handleDelete={handleDelete}
      btnPng={pngFile}
      setBtnPng={setPngFile}
      handleUploadClickSound={handleUploadClickSound}
      clickSoundFile={clickSoundFile}
      setClickSoundFile={setClickSoundFile}
      setPromoVideoFile={setPromoVideoFile}
      subcategories={subcategories}
      locations={locations}
      onSubcategoriesChange={(selectedIds) => {
        console.log("Selected subcategories:", selectedIds);
      }}
      onLocationsChange={(selectedIds) => {
        console.log("Selected locations:", selectedIds);
      }}
      selectedExistingSubCategories={selectedExistingSubCategories}
      setSelectedExistingSubCategories={setSelectedExistingSubCategories}
      currentLanguage={currentLanguage}
      stepByStepConfig={stepByStepConfig}
      setStepByStepConfig={setStepByStepConfig}
      saveStepByStepConfig={saveStepByStepConfig}
    />
  );
};

export default WayfinderCategory;
